import React, { useState, useCallback, useRef, useEffect } from 'react';
import { 
  PhotoIcon, 
  SparklesIcon, 
  XMarkIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  CloudArrowUpIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
  TrophyIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { AnalysisResult, ApiProvider } from '../../types';
import { uploadImageDirectly } from '../../services/apiService';
import { scanService } from '../../services/scanService';
import { statsEmitter } from '../../utils/statsEmitter';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import { useAnalysisPageVisibility } from '../../hooks/usePageVisibilityAnalysis';
import { useNewTour } from '../../contexts/NewTourContext';
import ResultCard from '../../components/ResultCard';
import DecodoResultCard from '../../components/DecodoResultCard';
import EnhancedAnalysisLoading from '../../components/EnhancedAnalysisLoading';

interface AnalysisPageBaseProps {
  apiProvider: ApiProvider;
  analyzeFunction: (imageUrl: string) => Promise<AnalysisResult>;
  pageTitle: string;
  pageDescription: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface Step {
  id: number;
  title: string;
  description: string;
  completed: boolean;
  active: boolean;
  icon: React.ComponentType<{ className?: string }>;
}

const AnalysisPageBase: React.FC<AnalysisPageBaseProps> = ({
  apiProvider,
  analyzeFunction,
  pageTitle,
  pageDescription,
  icon: Icon,
}) => {
  const { showToast } = useToast();
  const { isAuthenticated } = useAuth();
  const { isActive: isTourActive, nextStep, handleTargetClick } = useNewTour();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Page visibility handling for tab switching
  const {
    isVisible,
    wasAwayDuringAnalysis,
    shouldShowWarning,
    saveCurrentAnalysis,
    restoreAnalysisIfNeeded,
    markAnalysisComplete,
    clearAnalysisState
  } = useAnalysisPageVisibility(apiProvider);
  
  // Core state
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // UI Enhancement state
  const [progress, setProgress] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem('soundEnabled');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [showCelebration, setShowCelebration] = useState(false);
  const [tabSwitchWarning, setTabSwitchWarning] = useState(false);
  const [showEnhancedLoading, setShowEnhancedLoading] = useState(false);
  const [showNewAnalysisConfirm, setShowNewAnalysisConfirm] = useState(false);
  
  // Steps for progress tracking
  const steps: Step[] = [
    { id: 1, title: 'Upload', description: 'Select your image', completed: false, active: false, icon: PhotoIcon },
    { id: 2, title: 'Process', description: 'Upload to server', completed: false, active: false, icon: CloudArrowUpIcon },
    { id: 3, title: 'Analyze', description: `AI analysis with ${apiProvider}`, completed: false, active: false, icon: SparklesIcon },
    { id: 4, title: 'Results', description: 'View insights', completed: false, active: false, icon: CheckCircleIcon }
  ];

  // Sound effects
  const playSound = useCallback((type: 'click' | 'success' | 'error') => {
    if (!soundEnabled) return;
    
    const context = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = context.createOscillator();
    const gainNode = context.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(context.destination);
    
    switch (type) {
      case 'click':
        oscillator.frequency.setValueAtTime(800, context.currentTime);
        gainNode.gain.setValueAtTime(0.1, context.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, context.currentTime + 0.1);
        oscillator.start(context.currentTime);
        oscillator.stop(context.currentTime + 0.1);
        break;
      case 'success':
        oscillator.frequency.setValueAtTime(523, context.currentTime);
        oscillator.frequency.setValueAtTime(659, context.currentTime + 0.1);
        oscillator.frequency.setValueAtTime(784, context.currentTime + 0.2);
        gainNode.gain.setValueAtTime(0.2, context.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, context.currentTime + 0.3);
        oscillator.start(context.currentTime);
        oscillator.stop(context.currentTime + 0.3);
        break;
      case 'error':
        oscillator.frequency.setValueAtTime(200, context.currentTime);
        gainNode.gain.setValueAtTime(0.2, context.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, context.currentTime + 0.5);
        oscillator.start(context.currentTime);
        oscillator.stop(context.currentTime + 0.5);
        break;
    }
  }, [soundEnabled]);

  // Update steps based on current progress
  const updateSteps = useCallback((stepIndex: number) => {
    setCurrentStep(stepIndex);
  }, []);

  // Handle file selection
  const handleFileSelect = useCallback((file: File) => {
    setSelectedImage(file);
    setError(null);
    playSound('click');
    
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
    
    updateSteps(1);
  }, [playSound, updateSteps]);

  // Handle drag and drop
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    const imageFile = files.find(file => file.type.startsWith('image/'));
    
    if (imageFile) {
      handleFileSelect(imageFile);
    }
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  // File input handling
  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  }, [handleFileSelect]);

  const triggerFileInput = useCallback(() => {
    playSound('click');
    fileInputRef.current?.click();
  }, [playSound]);

  // Handle tour-aware clicks
  const handleTourClick = (_e: React.MouseEvent<HTMLElement>, tourAttribute?: string) => {
    if (isTourActive && tourAttribute) {
      console.log('🎯 AnalysisPageBase: Tour click detected for:', tourAttribute);
      handleTargetClick(tourAttribute);
    }
  };

  // Analysis function
  const analyzeImage = useCallback(async () => {
    if (!selectedImage) return;

    setIsProcessing(true);
    setShowEnhancedLoading(true);
    setError(null);
    setProgress(0);
    updateSteps(2);

    try {
      // Upload stage - Real progress tracking
      setProgress(5);
      await new Promise(resolve => setTimeout(resolve, 100));
      setProgress(10);
      
      const imageUrl = await uploadImageDirectly(selectedImage);
      
      setProgress(30);
      updateSteps(3);

      // Analysis stage - Start progress tracking during actual API call
      setProgress(35);
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Start progress animation during actual API call
      const analysisStartTime = Date.now();
      let progressInterval: NodeJS.Timeout | undefined;
      
      // Start real-time progress tracking
      let currentProgress = 35;
      progressInterval = setInterval(() => {
        const elapsedTime = Date.now() - analysisStartTime;
        
        // Progress more slowly and realistically
        if (elapsedTime < 2000) {
          // First 2 seconds: 35% to 45%
          currentProgress = Math.min(45, 35 + (elapsedTime / 2000) * 10);
        } else if (elapsedTime < 5000) {
          // Next 3 seconds: 45% to 60%
          currentProgress = Math.min(60, 45 + ((elapsedTime - 2000) / 3000) * 15);
        } else if (elapsedTime < 10000) {
          // Next 5 seconds: 60% to 80%
          currentProgress = Math.min(80, 60 + ((elapsedTime - 5000) / 5000) * 20);
        } else {
          // After 10 seconds: 80% to 90% (slower)
          currentProgress = Math.min(90, 80 + ((elapsedTime - 10000) / 10000) * 10);
        }
        
        setProgress(Math.floor(currentProgress));
      }, 200); // Update every 200ms for smooth animation

      // Execute the actual API call
      const result = await analyzeFunction(imageUrl);

      // Clear the progress interval
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      
      setProgress(95);
      updateSteps(4);
      
      // Final progress to 100%
      await new Promise(resolve => setTimeout(resolve, 300));
      setProgress(100);

      // Brief delay for visual feedback
      await new Promise(resolve => setTimeout(resolve, 500));

      setAnalysisResult(result);
      setIsProcessing(false);
      setShowEnhancedLoading(false);
      playSound('success');
      setShowCelebration(true);
      markAnalysisComplete(); // Clear persisted state since analysis is complete
      
      // Hide celebration after 3 seconds
      setTimeout(() => setShowCelebration(false), 3000);

      // Save to database if authenticated
      if (isAuthenticated) {
        try {
          await scanService.createScan({
            title: result.title,
            description: result.description,
            value: result.value,
            aiExplanation: result.aiExplanation,
            imageUrl: imageUrl,
            apiProvider: apiProvider,
            visualMatches: result.visualMatches,
          });
          statsEmitter.emit();
        } catch (saveError) {
          console.error('Failed to save scan:', saveError);
        }
      }

      showToast('success', `${apiProvider} Analysis Complete!`, 'Your image has been analyzed successfully.');
    } catch (err) {
      console.error(`Error analyzing with ${apiProvider}:`, err);
      const errorMessage = err instanceof Error ? err.message : `Failed to analyze image with ${apiProvider}.`;
      setError(errorMessage);
      setIsProcessing(false);
      setShowEnhancedLoading(false);
      setProgress(0);
      playSound('error');
      showToast('error', 'Analysis Failed', errorMessage);
    }
  }, [selectedImage, analyzeFunction, apiProvider, showToast, isAuthenticated, playSound, updateSteps]);

  // Determine current loading stage based on progress
  const getCurrentStage = (): 'uploading' | 'analyzing' | 'processing' | 'complete' => {
    if (currentStep <= 2) return 'uploading';
    if (currentStep === 3) return 'analyzing';
    if (currentStep === 4 && progress < 100) return 'processing';
    return 'complete';
  };

  const resetAnalysis = useCallback(() => {
    setSelectedImage(null);
    setImagePreview(null);
    setAnalysisResult(null);
    setError(null);
    setIsProcessing(false);
    setProgress(0);
    setCurrentStep(0);
    setShowCelebration(false);
    setTabSwitchWarning(false);
    setShowNewAnalysisConfirm(false);
    clearAnalysisState(); // Clear persisted state
    playSound('click');
  }, [playSound, clearAnalysisState]);

  const clearImage = useCallback(() => {
    setSelectedImage(null);
    setImagePreview(null);
    setError(null);
    setCurrentStep(0);
    setTabSwitchWarning(false);
    setShowEnhancedLoading(false);
    clearAnalysisState(); // Clear persisted state
    playSound('click');
  }, [playSound, clearAnalysisState]);

  // Toggle sound
  const toggleSound = useCallback(() => {
    const newSoundEnabled = !soundEnabled;
    setSoundEnabled(newSoundEnabled);
    localStorage.setItem('soundEnabled', JSON.stringify(newSoundEnabled));
    playSound('click');
  }, [soundEnabled, playSound]);

  // Save sound preference
  useEffect(() => {
    localStorage.setItem('soundEnabled', JSON.stringify(soundEnabled));
  }, [soundEnabled]);

  // Handle page visibility and save/restore analysis state
  useEffect(() => {
    // Check if we need to restore analysis state when component mounts
    if (wasAwayDuringAnalysis) {
      const restoredState = restoreAnalysisIfNeeded();
      if (restoredState) {
        if (restoredState.error) {
          setError(restoredState.error);
          setTabSwitchWarning(true);
        }
        if (restoredState.selectedImage) {
          // We don't restore the actual file, but we can show a message
          setTabSwitchWarning(true);
        }
        if (restoredState.imagePreview) {
          setImagePreview(restoredState.imagePreview);
        }
        if (restoredState.currentStep) {
          setCurrentStep(restoredState.currentStep);
        }
        if (restoredState.analysisResult) {
          setAnalysisResult(restoredState.analysisResult);
          markAnalysisComplete();
        }
      }
    }
  }, [wasAwayDuringAnalysis, restoreAnalysisIfNeeded, markAnalysisComplete]);

  // Set warning based on shouldShowWarning
  useEffect(() => {
    if (shouldShowWarning) {
      setTabSwitchWarning(true);
    }
  }, [shouldShowWarning]);

  // Save analysis state when tab becomes hidden during processing
  useEffect(() => {
    if (!isVisible && isProcessing) {
      saveCurrentAnalysis({
        selectedImage,
        imagePreview,
        isProcessing,
        currentStep,
        progress,
        error,
        analysisResult
      });
    }
  }, [isVisible, isProcessing, selectedImage, imagePreview, currentStep, progress, error, analysisResult, saveCurrentAnalysis]);

  // Tour advancement effect - automatically advance when analysis results appear
  useEffect(() => {
    if (isTourActive && analysisResult) {
      console.log('🎯 AnalysisPageBase: Analysis results appeared during tour, advancing to next step');
      setTimeout(() => {
        nextStep();
      }, 1000);
    }
  }, [analysisResult, isTourActive, nextStep]);

  // Clear warning when user dismisses it
  const dismissTabSwitchWarning = useCallback(() => {
    setTabSwitchWarning(false);
    clearAnalysisState();
  }, [clearAnalysisState]);

  // If we have results, show them
  if (analysisResult) {
    const handleSaveAnalysis = async () => {
      try {
        await scanService.createScan({
          title: analysisResult.title,
          description: analysisResult.description,
          value: analysisResult.value,
          aiExplanation: analysisResult.aiExplanation,
          imageUrl: analysisResult.imageUrl,
          apiProvider: analysisResult.apiProvider,
          visualMatches: analysisResult.visualMatches || [],
        });
        showToast('success', 'Saved', 'Analysis saved to your history.');
      } catch (error) {
        showToast('error', 'Save Failed', 'Could not save analysis.');
      }
    };
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-slate-900 dark:via-blue-900 dark:to-indigo-900">
        <div className="max-w-4xl mx-auto px-4 py-8 space-y-6" data-tour="tour-analysis-results">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {apiProvider} Analysis Results
            </h1>
            <div className="flex gap-2">
              <button
                onClick={(e) => {
                  handleTourClick(e, 'tour-save-analysis');
                  handleSaveAnalysis();
                }}
                className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors duration-200 space-x-2"
                data-tour="tour-save-analysis"
              >
                <CloudArrowUpIcon className="h-4 w-4" />
                <span>Save Analysis</span>
              </button>
              <button
                onClick={() => setShowNewAnalysisConfirm(true)}
                className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200 space-x-2"
              >
                <ArrowPathIcon className="h-4 w-4" />
                <span>Analyze Another</span>
              </button>
            </div>
          </div>
          {apiProvider === ApiProvider.DECODO ? (
            <DecodoResultCard result={analysisResult} />
          ) : (
            <ResultCard result={analysisResult} />
          )}

          {showNewAnalysisConfirm && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 px-4"
              role="dialog"
              aria-modal="true"
              aria-labelledby="new-analysis-title"
            >
              <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl dark:bg-gray-800">
                <h2 id="new-analysis-title" className="text-xl font-bold text-gray-900 dark:text-white">
                  Analyze another item?
                </h2>
                <p className="mt-2 text-gray-600 dark:text-gray-300">
                  This will clear the current item from the analyzer so you can upload a new one. Your completed result remains in your history.
                </p>
                <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                  <button
                    onClick={() => setShowNewAnalysisConfirm(false)}
                    className="rounded-lg px-4 py-2 font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                  >
                    Keep viewing results
                  </button>
                  <button
                    onClick={resetAnalysis}
                    className="rounded-lg bg-blue-600 px-4 py-2 font-medium text-white transition-colors hover:bg-blue-700"
                  >
                    Start new analysis
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Show enhanced loading when analysis is in progress
  if (showEnhancedLoading && isProcessing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-slate-900 dark:via-blue-900 dark:to-indigo-900">
        <EnhancedAnalysisLoading
          currentStage={getCurrentStage()}
          apiProvider={apiProvider}
          progress={progress}
          onComplete={() => setShowEnhancedLoading(false)}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-slate-900 dark:via-blue-900 dark:to-indigo-900" data-tour="tour-camera-page-container">
      {/* Sound Toggle */}
      <div className="fixed top-4 right-4 z-50">
        <button
          onClick={toggleSound}
          className={`p-3 rounded-full shadow-lg transition-all duration-200 ${
            soundEnabled 
              ? 'bg-blue-500 hover:bg-blue-600 text-white' 
              : 'bg-gray-300 hover:bg-gray-400 text-gray-600'
          }`}
          title={soundEnabled ? 'Disable sounds' : 'Enable sounds'}
        >
          {soundEnabled ? (
            <SpeakerWaveIcon className="h-5 w-5" />
          ) : (
            <SpeakerXMarkIcon className="h-5 w-5" />
          )}
        </button>
      </div>

      {/* Celebration Popup */}
      {showCelebration && (
        <div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none">
          <div className="bg-gradient-to-r from-green-400 to-green-600 text-white px-8 py-4 rounded-2xl shadow-2xl animate-bounce">
            <div className="flex items-center space-x-3">
              <TrophyIcon className="h-8 w-8" />
              <div>
                <h3 className="text-xl font-bold">Analysis Complete!</h3>
                <p className="text-green-100">Great results found</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab Switch Warning */}
      {tabSwitchWarning && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 mx-4 max-w-md shadow-2xl">
            <div className="flex items-start space-x-3">
              <ExclamationTriangleIcon className="h-6 w-6 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Analysis Interrupted
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Your analysis was interrupted when you switched tabs or minimized the browser. 
                  For best results, please keep this tab active during analysis.
                </p>
                <div className="flex space-x-3">
                  <button
                    onClick={dismissTabSwitchWarning}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                  >
                    Got it
                  </button>
                  <button
                    onClick={resetAnalysis}
                    className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors"
                  >
                    Start Over
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl mb-6">
            <Icon className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            {pageTitle}
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            {pageDescription}
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-12">
          <div className="flex items-center justify-center space-x-4">
            {steps.map((step, index) => {
              const StepIcon = step.icon;
              const isCompleted = index < currentStep;
              const isActive = index === currentStep;
              
              return (
                <div key={step.id} className="flex items-center">
                  <div className={`flex items-center space-x-2 px-4 py-2 rounded-full transition-all duration-300 ${
                    isCompleted 
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' 
                      : isActive 
                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' 
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-400'
                  }`}>
                    <StepIcon className="h-5 w-5" />
                    <span className="text-sm font-medium">{step.title}</span>
                    {isCompleted && <CheckCircleIcon className="h-4 w-4" />}
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`w-8 h-0.5 mx-2 ${
                      isCompleted ? 'bg-green-300' : 'bg-gray-200 dark:bg-gray-700'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Upload Progress */}
        {isProcessing && (
          <div className="mb-8">
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-4 mb-4">
                <div className="animate-spin">
                  <SparklesIcon className="h-6 w-6 text-blue-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    Processing with {apiProvider}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {currentStep === 2 ? 'Uploading image...' : 
                     currentStep === 3 ? 'Analyzing with AI...' : 
                     'Finalizing results...'}
                  </p>
                </div>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-blue-500 to-purple-600 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 text-center">
                {progress}% complete
              </p>
              
              {/* Tab switching warning */}
              <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <div className="flex items-start space-x-2">
                  <ExclamationTriangleIcon className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-800 dark:text-amber-200">
                    <strong>Tip:</strong> Keep this tab active for best results. Switching tabs may interrupt the analysis.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Image Preview */}
        {imagePreview && (
          <div className="mb-8">
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
              <div className="relative group">
                <img
                  src={imagePreview}
                  alt="Selected"
                  className="w-full max-h-96 object-contain rounded-xl"
                />
                <button
                  onClick={clearImage}
                  className="absolute top-3 right-3 bg-red-500 hover:bg-red-600 text-white p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-200"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Upload Area */}
        {!selectedImage && (
          <div className="mb-8">
            <div
              className={`relative border-2 border-dashed rounded-2xl p-12 transition-all duration-300 cursor-pointer group hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/10 ${
                isDragOver 
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                  : 'border-gray-300 dark:border-gray-600'
              }`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={(e) => {
                handleTourClick(e, 'tour-upload-image');
                triggerFileInput();
              }}
              data-tour="tour-upload-image"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileInputChange}
                className="hidden"
              />
              
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl mb-6 group-hover:scale-110 transition-transform duration-200">
                  <PhotoIcon className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  Upload Your Image
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Drag and drop an image here, or click to select
                </p>
                <div className="inline-flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                  <span>Supports JPG, PNG, WEBP</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="mb-8">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl p-6">
              <div className="flex items-start space-x-3">
                <ExclamationCircleIcon className="h-6 w-6 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-1">
                    Analysis Failed
                  </h3>
                  <p className="text-red-700 dark:text-red-300">
                    {error}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Analyze Button */}
        {selectedImage && !isProcessing && !error && (
          <div className="text-center">
            <button
              onClick={(e) => {
                handleTourClick(e, 'tour-analyze-image');
                analyzeImage();
              }}
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white text-lg font-semibold rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 space-x-3"
              data-tour="tour-analyze-image"
            >
              <SparklesIcon className="h-6 w-6" />
              <span>Analyze with {apiProvider}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalysisPageBase;
